/**
 * Utility functions for dynamic date filter options in sales funnel
 * Based on Salesforce/HubSpot industry standards
 */

export interface DateFilterOption {
  label: string;           // Display label: "This Month (Nov 2024)"
  value: string;           // API value: "this_month" or specific date range
  startDate: Date;         // Calculated start date
  endDate: Date;           // Calculated end date
  type: 'relative' | 'absolute';  // Relative (this month) or absolute (specific dates)
}

/**
 * Get current date (for testing purposes, can be overridden)
 */
export const getCurrentDate = (): Date => {
  return new Date();
};

/**
 * Format date as YYYY-MM-DD for SQL queries
 */
export const formatDateForSQL = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get month name from date
 */
export const getMonthName = (date: Date): string => {
  return date.toLocaleDateString('en-US', { month: 'short' });
};

/**
 * Get quarter number from date (1-4)
 */
export const getQuarter = (date: Date): number => {
  return Math.floor(date.getMonth() / 3) + 1;
};

/**
 * Get quarter label: "Q1 2025"
 */
export const getQuarterLabel = (date: Date): string => {
  return `Q${getQuarter(date)} ${date.getFullYear()}`;
};

/**
 * Get first day of quarter
 */
export const getQuarterStart = (date: Date): Date => {
  const quarter = getQuarter(date);
  const startMonth = (quarter - 1) * 3;
  return new Date(date.getFullYear(), startMonth, 1);
};

/**
 * Get last day of quarter
 */
export const getQuarterEnd = (date: Date): Date => {
  const quarter = getQuarter(date);
  const endMonth = quarter * 3;
  return new Date(date.getFullYear(), endMonth, 0); // Day 0 = last day of previous month
};

/**
 * Add months to a date
 */
export const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

/**
 * Get first day of month
 */
export const getMonthStart = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

/**
 * Get last day of month
 */
export const getMonthEnd = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

/**
 * Get first day of week (Sunday)
 */
export const getWeekStart = (date: Date): Date => {
  const result = new Date(date);
  const day = result.getDay(); // 0 = Sunday
  result.setDate(result.getDate() - day);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get last day of week (Saturday)
 */
export const getWeekEnd = (date: Date): Date => {
  const result = new Date(date);
  const day = result.getDay(); // 0 = Sunday
  result.setDate(result.getDate() + (6 - day));
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Get first day of year
 */
export const getYearStart = (date: Date): Date => {
  return new Date(date.getFullYear(), 0, 1);
};

/**
 * Get last day of year
 */
export const getYearEnd = (date: Date): Date => {
  return new Date(date.getFullYear(), 11, 31, 23, 59, 59, 999);
};

/**
 * Add weeks to a date
 */
export const addWeeks = (date: Date, weeks: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + (weeks * 7));
  return result;
};

/**
 * Generate all dynamic date filter options
 * Returns array of options sorted by chronological order
 */
export const getDateFilterOptions = (): DateFilterOption[] => {
  const now = getCurrentDate();
  const options: DateFilterOption[] = [];

  // 1. Last Quarter
  const lastQuarter = addMonths(now, -3);
  const lastQuarterStart = getQuarterStart(lastQuarter);
  const lastQuarterEnd = getQuarterEnd(lastQuarter);
  options.push({
    label: `Last Quarter (${getQuarterLabel(lastQuarter)})`,
    value: 'last_quarter',
    startDate: lastQuarterStart,
    endDate: lastQuarterEnd,
    type: 'relative',
  });

  // 2. Last 3 Months (rolling window)
  const last3MonthsStart = addMonths(getMonthStart(now), -3);
  const last3MonthsEnd = addMonths(getMonthEnd(now), -1); // End of last month
  options.push({
    label: 'Last 3 Months',
    value: 'last_3_months',
    startDate: last3MonthsStart,
    endDate: last3MonthsEnd,
    type: 'relative',
  });

  // 3. Last Month
  const lastMonth = addMonths(now, -1);
  const lastMonthStart = getMonthStart(lastMonth);
  const lastMonthEnd = getMonthEnd(lastMonth);
  options.push({
    label: `Last Month (${getMonthName(lastMonth)} ${lastMonth.getFullYear()})`,
    value: 'last_month',
    startDate: lastMonthStart,
    endDate: lastMonthEnd,
    type: 'relative',
  });

  // 4. This Week
  const thisWeekStart = getWeekStart(now);
  const thisWeekEnd = getWeekEnd(now);
  options.push({
    label: 'This Week',
    value: 'this_week',
    startDate: thisWeekStart,
    endDate: thisWeekEnd,
    type: 'relative',
  });

  // 5. This Month (Current month)
  const thisMonthStart = getMonthStart(now);
  const thisMonthEnd = getMonthEnd(now);
  options.push({
    label: `This Month (${getMonthName(now)} ${now.getFullYear()})`,
    value: 'this_month',
    startDate: thisMonthStart,
    endDate: thisMonthEnd,
    type: 'relative',
  });

  // 6. Next Week
  const nextWeekStart = getWeekStart(addWeeks(now, 1));
  const nextWeekEnd = getWeekEnd(addWeeks(now, 1));
  options.push({
    label: 'Next Week',
    value: 'next_week',
    startDate: nextWeekStart,
    endDate: nextWeekEnd,
    type: 'relative',
  });

  // 7. Next Month
  const nextMonth = addMonths(now, 1);
  const nextMonthStart = getMonthStart(nextMonth);
  const nextMonthEnd = getMonthEnd(nextMonth);
  options.push({
    label: `Next Month (${getMonthName(nextMonth)} ${nextMonth.getFullYear()})`,
    value: 'next_month',
    startDate: nextMonthStart,
    endDate: nextMonthEnd,
    type: 'relative',
  });

  // 8. Next 3 Months (rolling window starting from next month)
  const next3MonthsStart = addMonths(getMonthStart(now), 1); // Start of next month
  const next3MonthsEnd = addMonths(getMonthEnd(now), 3); // End of 3 months from now
  options.push({
    label: 'Next 3 Months',
    value: 'next_3_months',
    startDate: next3MonthsStart,
    endDate: next3MonthsEnd,
    type: 'relative',
  });

  // 9. This Quarter
  const thisQuarterStart = getQuarterStart(now);
  const thisQuarterEnd = getQuarterEnd(now);
  options.push({
    label: `This Quarter (${getQuarterLabel(now)})`,
    value: 'this_quarter',
    startDate: thisQuarterStart,
    endDate: thisQuarterEnd,
    type: 'relative',
  });

  // 10. Next Quarter
  const nextQuarter = addMonths(now, 3);
  const nextQuarterStart = getQuarterStart(nextQuarter);
  const nextQuarterEnd = getQuarterEnd(nextQuarter);
  options.push({
    label: `Next Quarter (${getQuarterLabel(nextQuarter)})`,
    value: 'next_quarter',
    startDate: nextQuarterStart,
    endDate: nextQuarterEnd,
    type: 'relative',
  });

  // 11. This Year
  const thisYearStart = getYearStart(now);
  const thisYearEnd = getYearEnd(now);
  options.push({
    label: `This Year (${now.getFullYear()})`,
    value: 'this_year',
    startDate: thisYearStart,
    endDate: thisYearEnd,
    type: 'relative',
  });

  return options;
};

/**
 * Get default filter option (Next Month)
 * NOTE: Changed from 'this_month' to 'next_month' to match dummy data (Nov-Dec 2025)
 */
export const getDefaultFilterOption = (): DateFilterOption => {
  const options = getDateFilterOptions();
  return options.find(opt => opt.value === 'next_month') || options[0];
};

/**
 * Convert DateFilterOption to SQL WHERE clause
 */
export const dateFilterToSQL = (option: DateFilterOption): string => {
  const startDateStr = formatDateForSQL(option.startDate);
  const endDateStr = formatDateForSQL(option.endDate);

  return `expected_close_date BETWEEN '${startDateStr}' AND '${endDateStr}'`;
};
